<template>
  <div class="purchase-cargo-insurance-quote-detail">
    <span>{{ t('cargoInsurance.quote.reviewPrompt') }}</span>
    <div
      class="mt-4 p-4 border-1 border-round border-200 flex flex-column gap-4"
    >
      <CargoInsuranceCertificateDetail
        :cargo-insurance-certificate="cargoInsuranceQuote"
      />

      <!-- statement of fact -->
      <div class="my-2 py-4 px-3 surface-50 flex flex-column gap-3">
        <h3>{{ t('cargoInsurance.statementOfFact') }}</h3>
        <p>
          {{ cargoInsuranceQuote.statementOfFact }}
        </p>
      </div>

      <!-- exclusions -->
      <h3 v-if="cargoInsuranceQuote.commodityExclusions?.length" class="my-2">
        {{ t('cargoInsurance.quote.commodityExclusion') }}
      </h3>
      <div
        v-for="(exclusion, i) of cargoInsuranceQuote.commodityExclusions"
        :key="i"
        class="my-2 py-4 px-3 surface-50 flex flex-column gap-3"
      >
        <h4>{{ exclusion.name }}</h4>
        <p>
          {{ exclusion.description }}
        </p>
        <div class="flex align-items-center">
          <Checkbox
            :model-value="commodityExclusionAcknowledgements[exclusion.id]"
            :input-id="`${uid}-exclusion-${i}`"
            binary
            @update:model-value="
              (isAcknowledged: boolean) =>
                $emit('update:commodityExclusionAcknowledgements', {
                  ...commodityExclusionAcknowledgements,
                  [exclusion.id]: isAcknowledged,
                })
            "
          />
          <label :for="`${uid}-exclusion-${i}`">
            {{ t('cargoInsurance.quote.exclusionAcknowledgement') }}
          </label>
        </div>
      </div>

      <!-- terms & conditions -->
      <div class="my-2 py-4 px-3 surface-50 flex flex-column gap-3">
        <h3>
          {{ t('cargoInsurance.termsAndConditions.termsAndConditionsHeader') }}
        </h3>
        <div class="flex align-items-center">
          <Checkbox
            :model-value="termsAndConditionsAcknowledgement"
            :input-id="`${uid}-terms-and-conditions`"
            binary
            @update:model-value="
              (isAcknowledged: boolean) =>
                $emit(
                  'update:termsAndConditionsAcknowledgement',
                  isAcknowledged,
                )
            "
          />
          <label :for="`${uid}-terms-and-conditions`">
            <span>
              {{
                t('cargoInsurance.termsAndConditions.acknowledgement.prefix')
              }}
            </span>
            <span>
              <a
                :href="cargoInsuranceQuote.termsAndConditionsLink"
                target="_blank"
              >
                {{ t('cargoInsurance.termsAndConditions.linkText') }}
              </a>
            </span>
            <span>
              {{
                t('cargoInsurance.termsAndConditions.acknowledgement.suffix')
              }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';

import { CargoInsuranceQuote } from '@/model/cargoInsurance';

import CargoInsuranceCertificateDetail from '@/components/cargoInsurance/CargoInsuranceCertificateDetail.vue';
import Checkbox from 'primevue/checkbox';

const props = defineProps<{
  cargoInsuranceQuote: CargoInsuranceQuote;
  commodityExclusionAcknowledgements: Record<string, boolean>;
  termsAndConditionsAcknowledgement: boolean;
}>();

defineEmits<{
  (
    e: 'update:commodityExclusionAcknowledgements',
    p: Record<string, boolean>,
  ): void;
  (e: 'update:termsAndConditionsAcknowledgement', p: boolean): void;
}>();

const { cargoInsuranceQuote } = toRefs(props);

const { t } = useI18n();
const { uid } = useUid('purchase-cargo-insurance-quote-detail');
</script>
